import { TEF_READING_FULL_STRUCTURE } from "@/data/tefReadingStructure";
import { selectRandomItems } from "@/utils/testRandomizer";

export const TEF_READING_FULL_BLUEPRINT = [
  {
    key: "daily_life_documents",
    label: "Documents de la vie quotidienne",
    count: 7,
  },
  {
    key: "phrases_lacunaires",
    label: "Phrases lacunaires",
    count: 4,
  },
  {
    key: "textes_lacunaires",
    label: "Textes lacunaires",
    count: 6,
  },
  {
    key: "lecture_rapide_textes",
    label: "Lecture rapide de textes",
    count: 4,
  },
  {
    key: "lecture_rapide_graphiques",
    label: "Lecture rapide de graphiques",
    count: 1,
  },
  {
    key: "documents_administratifs",
    label: "Documents administratifs et professionnels",
    count: 10,
  },
  {
    key: "articles_presse",
    label: "Articles de presse",
    count: 8,
  },
];

export function getCleanSectionName(section: any): string {
  const value = String(section || "").trim();
  const normalized = value.toLowerCase();

  if (normalized.includes("documents administratifs")) {
    return "Documents administratifs et professionnels";
  }
  if (normalized.includes("articles de presse")) {
    return "Articles de presse";
  }
  if (normalized.includes("documents de la vie quotidienne") || normalized.includes("documents divers")) {
    return "Documents de la vie quotidienne";
  }
  if (normalized.includes("phrases") && normalized.includes("textes") && normalized.includes("lacunaires")) {
    return "Phrases et textes lacunaires";
  }
  if (normalized.includes("lecture rapide")) {
    return "Lecture rapide de textes et de graphiques";
  }

  return value;
}

export function getCleanSectionLabel(question: any): string {
  const subSection =
    question.metadata?.subSection ||
    question.metadata?.sub_section ||
    question.subSection ||
    question.sub_section ||
    question.questionSubtype ||
    "";

  const section = question.section || question.category || "";
  const normalizedSub = String(subSection).toLowerCase();

  if (normalizedSub === "lecture_rapide_textes") {
    return "Lecture rapide de textes";
  }
  if (normalizedSub === "lecture_rapide_graphiques") {
    return "Lecture rapide de graphiques";
  }
  if (normalizedSub === "phrases_lacunaires") {
    return "Phrases lacunaires";
  }
  if (normalizedSub === "textes_lacunaires") {
    return "Textes lacunaires";
  }

  return getCleanSectionName(section);
}

function normalizeText(val: unknown): string {
  return String(val || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

export function normalizeSection(value: unknown): string {
  return normalizeText(value);
}

export function normalizeSubSection(question: any): string {
  if (!question) return "";
  return normalizeText(
    question.subSection ??
    question.sub_section ??
    question.questionSubtype ??
    question.question_subtype ??
    question.metadata?.subSection ??
    question.metadata?.sub_section ??
    ""
  );
}

export function getReadingFullTestBucket(question: any): string {
  const sectionStr = normalizeText(question.section ?? question.category ?? "");
  const subSectionStr = normalizeSubSection(question);

  // 1. Phrases et textes lacunaires
  if (sectionStr.includes("phrases") || sectionStr.includes("textes") || sectionStr.includes("lacunaires")) {
    if (subSectionStr.includes("phrase")) return "phrases_lacunaires";
    if (subSectionStr.includes("texte")) return "textes_lacunaires";
    
    // fallbacks se a subseção não veio explícita
    if (sectionStr.includes("phrase lacunaire")) return "phrases_lacunaires";
    if (sectionStr.includes("texte lacunaire")) return "textes_lacunaires";
    
    // verificação pelo título ou conteúdo
    const titleStr = normalizeText(question.title);
    if (titleStr.includes("phrase") || titleStr.includes("q1") || titleStr.includes("q2") || titleStr.includes("q3") || titleStr.includes("q4")) {
      return "phrases_lacunaires";
    }
    return "textes_lacunaires";
  }

  // 2. Lecture rapide de textes et de graphiques
  if (sectionStr.includes("lecture") || sectionStr.includes("rapide") || sectionStr.includes("graphique")) {
    if (subSectionStr.includes("graphique") || subSectionStr.includes("graphi")) return "lecture_rapide_graphiques";
    if (subSectionStr.includes("texte")) return "lecture_rapide_textes";

    // fallback: se tiver imagem, provável que seja graphique
    const hasImage =
      question.supportImage ||
      question.support_image ||
      question.imageUrl ||
      question.image_url ||
      question.mediaUrl ||
      question.media_url ||
      question.metadata?.supportImage ||
      question.metadata?.imageUrl ||
      question.hasImage;

    if (hasImage) return "lecture_rapide_graphiques";
    return "lecture_rapide_textes";
  }

  // 3. Demais seções
  if (sectionStr.includes("quotidienne") || sectionStr.includes("divers") || sectionStr.includes("section a")) {
    return "daily_life_documents";
  }
  if (sectionStr.includes("administratif") || sectionStr.includes("professionnel") || sectionStr.includes("admin")) {
    return "documents_administratifs";
  }
  if (sectionStr.includes("article") || sectionStr.includes("presse") || sectionStr.includes("section c")) {
    return "articles_presse";
  }

  return "daily_life_documents";
}

export function buildFullReadingTestQuestions(allReadingQuestions: any[], randomSeed: string): any[] {
  const result: any[] = [];
  const remainingPool = [...allReadingQuestions];

  const removeFromRemaining = (q: any) => {
    const idx = remainingPool.findIndex(r => r.id === q.id);
    if (idx !== -1) remainingPool.splice(idx, 1);
  };

  const configStr = localStorage.getItem("tef_trainer_settings");
  const config = configStr ? JSON.parse(configStr) : {};
  const shuffleQuestions = config.shuffle !== false;

  for (const block of TEF_READING_FULL_BLUEPRINT) {
    const pool = remainingPool.filter(q => getReadingFullTestBucket(q) === block.key);
    
    const selected = selectRandomItems(pool, block.count);

    if (selected.length < block.count) {
      console.warn("[ReadingFullTest] Questões insuficientes", {
        bucket: block.key,
        required: block.count,
        available: selected.length,
      });
    }

    selected.forEach(q => {
      removeFromRemaining(q);
      result.push({
        ...q,
        displaySection: block.label,
        displaySubSection: block.label,
        sectionKey: block.key
      });
    });
  }

  // Fallback to reach 40 if needed
  if (result.length < 40 && remainingPool.length > 0) {
    const deficit = 40 - result.length;
    const fallbackSelected = selectRandomItems(remainingPool, deficit);
    console.warn(`[ReadingFullTest] Preenchendo ${fallbackSelected.length} questões de outras seções para atingir 40.`);
    fallbackSelected.forEach(q => {
      result.push({
        ...q,
        displaySection: "Geral",
        displaySubSection: "Geral"
      });
    });
  }

  console.log("[ReadingFullTest] total selected", result.length);
  return result;
}

export function buildSectionExamQuestions(allReadingQuestions: any[], selectedSection: string, randomSeed: string): any[] {
  const buckets: Record<string, any[]> = {
    daily_life_documents: [],
    phrases_lacunaires: [],
    textes_lacunaires: [],
    lecture_rapide_textes: [],
    lecture_rapide_graphiques: [],
    documents_administratifs: [],
    articles_presse: []
  };

  allReadingQuestions.forEach(q => {
    const bucket = getReadingFullTestBucket(q);
    if (buckets[bucket]) buckets[bucket].push(q);
  });

  const configStr = localStorage.getItem("tef_trainer_settings");
  const config = configStr ? JSON.parse(configStr) : {};
  const shuffleQuestions = config.shuffle !== false;

  const pickQuestions = (arr: any[], count: number, bucketKey: string, label: string) => {
    return selectRandomItems(arr, count).map(q => ({
      ...q,
      displaySection: label,
      displaySubSection: label,
      sectionKey: bucketKey
    }));
  };

  const normalized = normalizeSection(selectedSection);

  // 1. Phrases et textes lacunaires (4 + 6)
  if (normalized.includes("phrases") || normalized.includes("textes") || normalized.includes("lacunaires")) {
    return [
      ...pickQuestions(buckets.phrases_lacunaires, 4, "phrases_lacunaires", "Phrases lacunaires"),
      ...pickQuestions(buckets.textes_lacunaires, 6, "textes_lacunaires", "Textes lacunaires")
    ];
  }

  // 2. Lecture rapide (4 + 1)
  if (normalized.includes("lecture") || normalized.includes("rapide") || normalized.includes("graphique")) {
    return [
      ...pickQuestions(buckets.lecture_rapide_textes, 4, "lecture_rapide_textes", "Lecture rapide de textes"),
      ...pickQuestions(buckets.lecture_rapide_graphiques, 1, "lecture_rapide_graphiques", "Lecture rapide de graphiques")
    ];
  }

  // 3. Documents administratifs (10)
  if (normalized.includes("administratif") || normalized.includes("professionnel")) {
    return pickQuestions(buckets.documents_administratifs, 10, "documents_administratifs", "Documents administratifs et profissionais");
  }

  // 4. Articles de presse (8)
  if (normalized.includes("article") || normalized.includes("presse")) {
    return pickQuestions(buckets.articles_presse, 8, "articles_presse", "Articles de presse");
  }

  // 5. Documents de la vie quotidienne (7)
  if (normalized.includes("quotidienne") || normalized.includes("divers")) {
    return pickQuestions(buckets.daily_life_documents, 7, "daily_life_documents", "Documents de la vie quotidienne");
  }

  // Fallback: return all available for that section if no specific count is matched
  const fallbackArr = allReadingQuestions.filter(q => normalizeSection(q.section) === normalized);
  return selectRandomItems(fallbackArr, fallbackArr.length);
}

/**
 * Montagem equilibrada para Diagnóstico (12 questões)
 */
export function buildDiagnosticQuestions(allQuestions: any[], randomSeed: string): any[] {
  const buckets: Record<string, any[]> = {
    daily_life_documents: [],
    phrases_lacunaires: [],
    textes_lacunaires: [],
    lecture_rapide_textes: [],
    lecture_rapide_graphiques: [],
    documents_administratifs: [],
    articles_presse: []
  };

  allQuestions.forEach(q => {
    const bucket = getReadingFullTestBucket(q);
    if (buckets[bucket]) buckets[bucket].push(q);
  });

  const configStr = localStorage.getItem("tef_trainer_settings");
  const config = configStr ? JSON.parse(configStr) : {};

  const pick = (arr: any[], count: number, bucketKey: string) => {
    return selectRandomItems(arr, count);
  };

  // Amostra equilibrada (12 questões)
  const sample = [
    ...pick(buckets.daily_life_documents, 2, "daily_life_documents"),
    ...pick(buckets.phrases_lacunaires, 2, "phrases_lacunaires"),
    ...pick(buckets.textes_lacunaires, 2, "textes_lacunaires"),
    ...pick(buckets.lecture_rapide_textes, 2, "lecture_rapide_textes"),
    ...pick(buckets.lecture_rapide_graphiques, 1, "lecture_rapide_graphiques"),
    ...pick(buckets.documents_administratifs, 2, "documents_administratifs"),
    ...pick(buckets.articles_presse, 1, "articles_presse")
  ];

  return selectRandomItems(sample, sample.length);
}

/**
 * Checks if the given value refers to the Writing competency (Expression Écrite).
 */
export function isWritingCompetency(value: unknown): boolean {
  const v = String(value || "").toLowerCase();
  return (
    v === "ee" ||
    v.includes("expression ecrite") ||
    v.includes("expression écrite") ||
    v.includes("writing")
  );
}

/**
 * Checks if the section name indicates a full Writing test (Section A + Section B).
 */
export function isFullWritingSection(section: unknown): boolean {
  const value = String(section || "").toLowerCase();
  return (
    value.includes("teste completo ee") ||
    value.includes("section a + section b") ||
    value.includes("full writing") ||
    value.includes("complete writing") ||
    value.includes("writing full") ||
    (value.includes("teste completo") && value.includes("ee")) ||
    (value.includes("simulado completo") && value.includes("ee"))
  );
}

/**
 * Checks if the question belongs to Writing Section A.
 */
export function isWritingSectionA(question: any): boolean {
  if (!question) return false;
  const values = [
    question.section,
    question.subSection,
    question.sub_section,
    question.metadata?.section,
    question.metadata?.subSection,
    question.sectionKey,
    question.title,
    question.type,
  ]
    .filter(Boolean)
    .map((v) => String(v).toLowerCase());

  return values.some((v) => 
    v === "section a" || 
    v === "section_a" || 
    v === "a" || 
    v.includes("section a") ||
    v.includes("fait divers") ||
    v.includes("tache 1") ||
    v.includes("task 1")
  );
}

/**
 * Checks if the question belongs to Writing Section B.
 */
export function isWritingSectionB(question: any): boolean {
  if (!question) return false;
  const values = [
    question.section,
    question.subSection,
    question.sub_section,
    question.metadata?.section,
    question.metadata?.subSection,
    question.sectionKey,
    question.title,
    question.type,
  ]
    .filter(Boolean)
    .map((v) => String(v).toLowerCase());

  return values.some((v) => 
    v === "section b" || 
    v === "section_b" || 
    v === "b" || 
    v.includes("section b") ||
    v.includes("argumentation") ||
    v.includes("opinion") ||
    v.includes("tache 2") ||
    v.includes("task 2")
  );
}
